import { arrow, autoUpdate, computePosition, flip, limitShift, offset, platform, shift } from '@floating-ui/dom';
import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { offsetParent } from 'composed-offset-position';
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from '../../common/colorable-component';
import { TooltipSchemeConfiguration } from '../../constants/colors';
import { TOOLTIP_DEFAULT_CONTENT_ALIGNMENT } from '../../constants/global-constants';
import { KeyIds } from '../../constants/keys';
import { isMobile } from '../../utils/screen-helper';
import { detectSlotChanges } from '../../utils/slot-helper';
import pfx from '../../utils/style-class-prefix-helper';
import { isTouchDevice } from '../../utils/touch-helper';
import {
  TOOLTIP_DEFAULT,
  TOOLTIP_PLACEMENT_BOTTOM,
  TOOLTIP_PLACEMENT_RIGHT,
  TOOLTIP_SCHEME_CONFIG,
  TOOLTIP_SCHEME_MAP,
  TOOLTIP_VISIBILITY_ALWAYS,
  TOOLTIP_VISIBILITY_MANUAL,
  TOOLTIP_VISIBILITY_ON_CLICK,
  TOOLTIP_VISIBILITY_ON_HOVER,
} from './constants/tooltip-constants';
import {
  TooltipContentAlignment,
  TooltipPlacement,
  TooltipSchemeDefinition,
  TooltipSchemeName,
  TooltipSize,
  TooltipVisibility,
} from './constants/tooltip-types';

import { StyledComponent } from '../../decorators/styled-component';
import { BackgroundColor } from '../background/constants/background-types';

// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: 'envc-eon-ui-tooltip',
  styleUrl: 'tooltip.scss',
  shadow: true,
})
export class Tooltip implements ColorableComponent<TooltipSchemeConfiguration<TooltipSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: TooltipSchemeConfiguration<TooltipSchemeDefinition> = TOOLTIP_SCHEME_CONFIG;
  schemeMap = TOOLTIP_SCHEME_MAP;

  private internalConnectedElement;
  arrow;
  floatingUI;
  mouseLeaveTimeout;
  parentMouseLeaveEventListenerFunction = this.parentMouseLeaveFn.bind(this);
  parentMouseEnterEventListenerFunction = this.parentMouseEnterFn.bind(this);
  parentClickEventListenerFunction = this.parentClickFn.bind(this);
  isFirstLoad: boolean = true;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or envc-eon-ui-background component.
   */
  @State() activeScheme: TooltipSchemeDefinition;

  @State() adjustedPlacement: TooltipPlacement = TOOLTIP_PLACEMENT_RIGHT;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEnvcEonUiTooltipElement;
  componentWrapper!: HTMLElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or envc-eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: TooltipSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * content alignment of slottet components.
   * @example left | center | right
   */
  @Prop({ reflect: true }) contentAlignment: TooltipContentAlignment = TOOLTIP_DEFAULT_CONTENT_ALIGNMENT;

  /**
   * The placement type of the tooltip
   */
  @Prop({ reflect: true }) placement: TooltipPlacement = TOOLTIP_PLACEMENT_RIGHT;

  /**
   * The visibility type of the tooltip. If mode is manual the developer has to handle the visibility via visible attribute.
   */
  @Prop({ reflect: true, mutable: true }) visibility: TooltipVisibility = TOOLTIP_VISIBILITY_ON_HOVER;

  /**
   * Is the tooltip visible or not?
   */
  @Prop({ mutable: true }) visible: boolean = false;

  /**
   * Set the size of tooltip
   */
  @Prop({ mutable: true }) size: TooltipSize = TOOLTIP_DEFAULT;

  /**
   * Set the time delay off if not required
   */
  @Prop({ mutable: true }) noTransitionDelay: boolean = false;

  /**
   * If tooltip should be connected to another element than the parent, you can set it here.
   */
  @Prop() connectedElement: HTMLElement;

  /**
   * ...
   */
  @Prop() optimizedPlacement: boolean = true;

  /**
   * Moves the tooltip to end of document to avoid a problem with stacking context. If you set this to false, the tooltip will stay where it is defined. This is especially helpful when a stable reference is needed.
   */
  @Prop() moveTooltipToEndOfBody: boolean = false;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  /**
   * ENVIAM Addon: A way to remove the orphaned tooltip (that way moved to the end of body) after its info-icon is gone
   */
  @Method()
  async removeTooltip() {
    this.hostElement.remove();
  }

  /**
   * Raised when the component has been clicked.
   */
  @Event() tooltipClick: EventEmitter<string>;

  componentWillLoad() {
    this.applyScheme();
    this.setInternalConnectedElement();

    // aria-describedby="tooltip" ADD TO PARENT with unique id?
  }

  componentDidLoad() {
    this.setCssVariables();
    this.initTooltipPlugin();
    this.activateTooltipVisibility();
    this.updatePlacement();
    this.applySize();

    detectSlotChanges(() => {
      this.updatePlacement();
    }, this.hostElement);
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  @Watch('scheme')
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch('activeScheme')
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.backgroundColor;
  }

  setInternalConnectedElement() {
    if (this.connectedElement) {
      this.internalConnectedElement = this.connectedElement;
    } else {
      this.internalConnectedElement = this.hostElement.parentElement;
      // enviaM: added isFirstLoad Clause - Otherwise tooltip is appended several times to the end of the body
      // in scenarios where componentWillLoad is triggered several times.
      if (this.moveTooltipToEndOfBody) {
        // checkup for storybook special case as pages are lazy loaded
        if (document.getElementById('storybook-root')) {
          document.getElementById('storybook-root').append(this.hostElement);
        } else {
          document.body.append(this.hostElement);
        }
      }
    }
  }

  @Watch('connectedElement')
  updateConnectedElement() {
    this.setInternalConnectedElement();
    this.floatingUI(); // destroy
    this.initTooltipPlugin();
  }

  @Watch('placement')
  updatePlacement() {
    this.adjustedPlacement = isMobile() && this.optimizedPlacement ? TOOLTIP_PLACEMENT_BOTTOM : this.placement;
    this.initTooltipPlugin();
  }

  @Watch('visible')
  handleInitialPositioning() {
    // reposition with first load to ensure exact position.
    if (this.isFirstLoad) {
      this.isFirstLoad = false;
      this.updatePlacement();
    }
  }

  @Watch('size')
  applySize() {
    this.updatePlacement();
  }

  @Watch('visibility')
  updateVisiblity() {
    this.activateTooltipVisibility();
  }

  @Listen('resize', { target: 'window' })
  onWindowResize() {
    this.updatePlacement();
  }

  @Listen('keydown', { target: 'document' })
  onFocusChange(event: KeyboardEvent) {
    if (event.key === KeyIds.TAB && this.visible && this.visibility !== 'always') {
      window.setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement;
        const isTooltip =
          activeElement === this.hostElement ||
          activeElement.querySelector('envc-eon-ui-tooltip')?.parentElement === activeElement ||
          this.hostElement.contains(activeElement);
        if (!isTooltip) {
          this.visible = false;
        }
      }, 0);
    }
  }

  private initTooltipPlugin() {
    //enviaM Fix - sometimes the placement watcher triggers earlier than internalConnectedElement is set
    if (!this.internalConnectedElement) {
      return;
    }

    this.floatingUI = autoUpdate(this.internalConnectedElement, this.hostElement, () => {
      computePosition(this.internalConnectedElement, this.hostElement, {
        platform: {
          ...platform,
          getOffsetParent: (element) => platform.getOffsetParent(element, offsetParent),
        },
        strategy: 'fixed', // 'absolute' by default
        placement: this.adjustedPlacement, // 'bottom' by default
        middleware: [
          flip(),
          shift({ padding: 12, limiter: limitShift({ offset: 16 }) }),
          arrow({ element: this.arrow, padding: 16 }),
          offset(9.5),
        ],
      }).then(({ x, y, placement, middlewareData }) => {
        const { x: arrowX, y: arrowY } = middlewareData.arrow;
        const staticSide = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right',
        }[placement.split('-')[0]];

        Object.assign(this.arrow.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
          right: '',
          bottom: '',
          [staticSide]: '-4px',
        });

        Object.assign(this.hostElement.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    });
  }

  private activateTooltipVisibility() {
    //enviaM Fix - sometimes the placement watcher triggers earlier than internalConnectedElement is set
    if (!this.internalConnectedElement) {
      return;
    }

    this.removeEventListeners();

    if (isTouchDevice() && this.visibility === TOOLTIP_VISIBILITY_ON_HOVER) {
      this.visibility = TOOLTIP_VISIBILITY_ON_CLICK;
      this.internalConnectedElement.addEventListener('mouseleave', this.parentMouseLeaveEventListenerFunction);
    }
    switch (this.visibility) {
      case TOOLTIP_VISIBILITY_ON_HOVER:
        this.visible = false;
        this.attachOpenOnHoverFunctionalityToParent();
        this.attachOpenOnHoverFunctionalityToTooltip();
        break;
      case TOOLTIP_VISIBILITY_ON_CLICK:
        this.visible = false;
        this.attachOpenOnClickFunctionalityToParent();
        break;
      case TOOLTIP_VISIBILITY_ALWAYS:
        // wait a bit on first rendering to ensure correct position.
        if (this.isFirstLoad) {
          setTimeout(() => {
            this.visible = true;
          }, 1000);
        } else {
          this.visible = true;
        }
        break;
      case TOOLTIP_VISIBILITY_MANUAL:
        this.visible = false;
        break;
    }
  }

  /*
   * Closes tooltip after mouse leave at the bottom (400ms)
   */
  private parentMouseLeaveFn() {
    if (!this.noTransitionDelay) {
      this.mouseLeaveTimeout = setTimeout(() => {
        this.visible = false;
      }, 400);
    } else {
      this.visible = false;
    }
  }

  /*
   * Interrupts mouse leave function if reentering parent or tooltip (400ms)
   */
  private parentMouseEnterFn() {
    this.visible = true;
    if (this.mouseLeaveTimeout) {
      clearTimeout(this.mouseLeaveTimeout);
    }
  }

  /*
   * Interrupts mouse leave function if reentering parent or tooltip (400ms)
   */
  private parentClickFn(e) {
    this.hostElement.addEventListener('mouseleave', this.parentMouseLeaveEventListenerFunction);
    this.hostElement.addEventListener('mouseenter', this.parentMouseEnterEventListenerFunction);

    const target = e.target as HTMLElement;

    if (this.visible) {
      if (target !== this.hostElement && !this.hostElement.contains(target)) {
        this.visible = false;
      }
    } else {
      this.visible = true;
    }

    this.tooltipClick.emit();
  }

  private attachOpenOnHoverFunctionalityToParent() {
    this.internalConnectedElement.addEventListener('mouseleave', this.parentMouseLeaveEventListenerFunction);
    this.internalConnectedElement.addEventListener('mouseenter', this.parentMouseEnterEventListenerFunction);
  }

  private attachOpenOnClickFunctionalityToParent() {
    this.internalConnectedElement.addEventListener('click', this.parentClickEventListenerFunction);
  }

  private removeEventListeners() {
    this.internalConnectedElement.removeEventListener('mouseleave', this.parentMouseLeaveEventListenerFunction);
    this.internalConnectedElement.removeEventListener('mouseenter', this.parentMouseEnterEventListenerFunction);
    this.hostElement.removeEventListener('mouseleave', this.parentMouseLeaveEventListenerFunction);
    this.hostElement.removeEventListener('mouseenter', this.parentMouseEnterEventListenerFunction);
    this.internalConnectedElement.removeEventListener('click', this.parentClickEventListenerFunction);
  }

  private attachOpenOnHoverFunctionalityToTooltip() {
    if (this.moveTooltipToEndOfBody) {
      this.hostElement.addEventListener('mouseenter', this.parentMouseEnterEventListenerFunction);
      this.hostElement.addEventListener('mouseleave', this.parentMouseLeaveEventListenerFunction);
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case KeyIds.ESCAPE:
        if (this.visible) {
          this.parentMouseLeaveFn();
          e.preventDefault();
          break;
        }
    }
  }

  render() {
    return (
      <Host
        role="tooltip"
        class={pfx({
          visible: this.visible,
          'apply-transition': !this.noTransitionDelay,
        })}
      >
        <div
          class={pfx({
            wrapper: true,
            visible: this.visible,
            'small-size': this.size === 'small',
          })}
          onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}
        >
          <div class={pfx({ inner: true, [`alignment-${this.contentAlignment}`]: this.contentAlignment })}>
            <slot />
          </div>
          <div
            ref={(el) => (this.arrow = el as HTMLElement)}
            class={pfx({ arrow: true, 'small-size': this.size === 'small' })}
          ></div>
        </div>
      </Host>
    );
  }
}
